import { bankPayments } from '@huggg/back-bar';
import initialStateLoader from '../initialState';

const INITIATE = 'huggg/bankPayment/INITIATE';
const INITIATE_SUCCESS = 'huggg/bankPayment/INITIATE_SUCCESS';
const INITIATE_FAILURE = 'huggg/bankPayment/INITIATE_FAILURE';
const CLEAR_ERROR = 'huggg/bankPayment/CLEAR_ERROR';
const POLL_START = 'huggg/bankPayment/POLL_START';
const POLL_SUCCESS = 'huggg/bankPayment/POLL_SUCCESS';
const POLL_FAILURE = 'huggg/bankPayment/POLL_FAILURE';
const POLL_STOP = 'huggg/bankPayment/POLL_STOP';
const SET_SELECTED_ACCOUNT = 'huggg/bankPayment/SET_SELECTED_ACCOUNT';

export const types = {
  INITIATE,
  INITIATE_SUCCESS,
  INITIATE_FAILURE,
  CLEAR_ERROR,
  POLL_START,
  POLL_SUCCESS,
  POLL_FAILURE,
  POLL_STOP,
  SET_SELECTED_ACCOUNT
};

const { bankPayment: initialState } = initialStateLoader();

// Status values that mean the BankClaim has reached a terminal server-side state.
const TERMINAL_STATUSES = ['COMPLETED', 'FAILED'];
const POLL_INTERVAL_MS = 3000;

/* Reducer - Default Export
========================================================================== */
const reducer = (state = initialState, action) => {
  const { payload, type } = action;

  switch (type) {
    case INITIATE:
      return { ...state, initiating: true, error: null };

    case INITIATE_SUCCESS:
      return {
        ...state,
        initiating: false,
        requestId: payload.requestId,
        redirectUrl: payload.redirectUrl,
        error: null
      };

    case INITIATE_FAILURE:
      return { ...state, initiating: false, error: payload.error };

    case CLEAR_ERROR:
      return { ...state, error: null, pollError: null };

    case POLL_START:
      return { ...state, isPolling: true, pollError: null };

    case POLL_SUCCESS:
      return {
        ...state,
        isPolling: !TERMINAL_STATUSES.includes(payload.status),
        status: payload.status,
        hasToken: payload.hasToken,
        accounts: payload.accounts,
        bankClaimId: payload.bankClaimId,
        value: payload.value,
        currency: payload.currency,
        pollError: null
      };

    case POLL_FAILURE:
      return { ...state, pollError: payload.error };

    case POLL_STOP:
      return { ...state, isPolling: false };

    case SET_SELECTED_ACCOUNT:
      return { ...state, selectedAccount: payload };

    default:
      return state;
  }
};

export default reducer;

/* Action Creators
========================================================================== */
export const clearError = () => dispatch => dispatch({ type: CLEAR_ERROR });

/* Side Effects
========================================================================== */
export const initiateSession = hugggId => dispatch => {
  dispatch({ type: INITIATE });

  return bankPayments
    .initiateSession(hugggId)
    .then(({ requestId, redirectUrl }) => {
      dispatch({
        type: INITIATE_SUCCESS,
        payload: { requestId, redirectUrl }
      });
      return { requestId, redirectUrl };
    })
    .catch(err => {
      if (process.env.NODE_ENV === 'development' && !window.Cypress) {
        console.error(err);
      }
      dispatch({
        type: INITIATE_FAILURE,
        payload: { error: err?.response?.data ?? err }
      });
      throw err;
    });
};

// Module-scoped timer handle so unmount/stop can cancel an in-flight loop
// without leaking through Redux state (timers aren't serialisable).
let pollTimer = null;

const stopTimer = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

const pollOnce = (hugggId, dispatch) =>
  bankPayments
    .getClaimStatus(hugggId)
    .then(payload => {
      dispatch({ type: POLL_SUCCESS, payload });
      if (!TERMINAL_STATUSES.includes(payload.status)) {
        pollTimer = setTimeout(
          () => pollOnce(hugggId, dispatch),
          POLL_INTERVAL_MS
        );
      } else {
        pollTimer = null;
      }
      return payload;
    })
    .catch(err => {
      if (process.env.NODE_ENV === 'development' && !window.Cypress) {
        console.error(err);
      }
      dispatch({
        type: POLL_FAILURE,
        payload: { error: err?.response?.data ?? err }
      });
      // Don't terminate the loop on a transient failure — try again next tick.
      pollTimer = setTimeout(
        () => pollOnce(hugggId, dispatch),
        POLL_INTERVAL_MS
      );
    });

export const startPolling = hugggId => dispatch => {
  stopTimer();
  dispatch({ type: POLL_START });
  return pollOnce(hugggId, dispatch);
};

export const stopPolling = () => dispatch => {
  stopTimer();
  dispatch({ type: POLL_STOP });
};

// One-shot variant of pollOnce — fires the same GET, dispatches the same
// POLL_SUCCESS, but never schedules a follow-up tick. Used by the success
// screen on hard-reload (when BankPaymentRedemption never mounted, so the
// poll-on-mount lifecycle never ran).
export const fetchStatus = hugggId => dispatch =>
  bankPayments
    .getClaimStatus(hugggId)
    .then(payload => {
      dispatch({ type: POLL_SUCCESS, payload });
      return payload;
    })
    .catch(err => {
      if (process.env.NODE_ENV === 'development' && !window.Cypress) {
        console.error(err);
      }
      dispatch({
        type: POLL_FAILURE,
        payload: { error: err?.response?.data ?? err }
      });
    });

// Remembered for the success screen so it can render the destination account
// after the claim returns. Lives in Redux because the success screen renders
// via the voucher pipeline, not the BankPaymentRedemption tree.
export const setSelectedAccount = account => dispatch =>
  dispatch({ type: SET_SELECTED_ACCOUNT, payload: account });

/* Selectors
========================================================================== */
export const getIsInitiating = state => state.bankPayment.initiating;
export const getRedirectUrl = state => state.bankPayment.redirectUrl;
export const getRequestId = state => state.bankPayment.requestId;
export const getError = state => state.bankPayment.error;
export const getBankClaimStatus = state => state.bankPayment.status;
export const getBankAccounts = state => state.bankPayment.accounts;
export const getBankClaimId = state => state.bankPayment.bankClaimId;
export const getHasToken = state => state.bankPayment.hasToken;
export const getIsPolling = state => state.bankPayment.isPolling;
export const getPollError = state => state.bankPayment.pollError;
export const getSelectedAccount = state => state.bankPayment.selectedAccount;
export const getPaymentValue = state => state.bankPayment.value;
export const getPaymentCurrency = state => state.bankPayment.currency;

export const actions = {
  clearError,
  initiateSession,
  startPolling,
  stopPolling,
  fetchStatus,
  setSelectedAccount,
  getIsInitiating,
  getRedirectUrl,
  getRequestId,
  getError,
  getBankClaimStatus,
  getBankAccounts,
  getBankClaimId,
  getHasToken,
  getIsPolling,
  getPollError,
  getSelectedAccount,
  getPaymentValue,
  getPaymentCurrency
};
